// Browser logger that forwards to multiple destinations (Datadog, Console, etc.)
// Can be configured to use different logging providers independently.

type LogData = Record<string, any>;
type LogLevel = 'warn' | 'error' | 'info' | 'debug';
type Environment = 'development' | 'staging' | 'production';

declare global {
  interface Window {
    DD_LOGS?: {
      logger?: {
        warn: (message: string, data?: LogData) => void;
        error: (message: string, data?: LogData) => void;
        info?: (message: string, data?: LogData) => void;
        debug?: (message: string, data?: LogData) => void;
      };
    };
  }
}

export interface Logger {
  warn: (message: string, data?: LogData) => void;
  error: (message: string, data?: LogData) => void;
  info?: (message: string, data?: LogData) => void;
  debug?: (message: string, data?: LogData) => void;
}

/**
 * Logs messages to Datadog Browser Logs if available.
 * Silently fails if Datadog is not initialized or the log level is not supported.
 * This is intentionally not being exported directly, but can be used internally.
 *
 * @param level - The log level (warn, error, info)
 * @param message - The log message
 * @param data - Optional additional data to log
 */
export const logToDatadog = (level: LogLevel, message: string, data?: LogData): void => {
  if (window.DD_LOGS?.logger?.[level]) {
    window.DD_LOGS.logger[level]!(message, data);
  }
};

/**
 * Logs messages to browser console with configurable behavior.
 * Console logging is enabled when:
 * - Environment is not 'production', OR
 * - ?showError=true is in the URL
 * 
 * @param level - The log level (warn, error, info)
 * @param message - The log message
 * @param data - Optional additional data to log
 * @param options - Configuration options
 * @param options.prefix - Custom prefix for the log message (default: auto-generated from level)
 * @param options.currentEnvironment - Current environment (default: 'production')
 */
export const logToConsole = (level: LogLevel, message: string, data?: LogData, options: { 
  prefix?: string;
  currentEnvironment?: Environment;
} = {}): void => {
  const { prefix, currentEnvironment = 'production' } = options;
  
  // Check if console logging should be enabled
  const isNonProduction = currentEnvironment !== 'production';
  const hasShowErrorParam = typeof window !== 'undefined' && 
                           new URLSearchParams(window.location.search).get('showError') === 'true';
  
  const shouldLog = isNonProduction || hasShowErrorParam;
  
  if (shouldLog) {
    // Generate appropriate prefix for the log level
    const defaultPrefixes: Record<LogLevel, string> = { 
      warn: 'Warning:', 
      error: 'Error:', 
      info: 'Info:',
      debug: 'Debug:'
    };
    const logPrefix = prefix || defaultPrefixes[level] || `${level.charAt(0).toUpperCase() + level.slice(1)}:`;
    console[level](logPrefix, message, data);
  }
};

/**
 * Creates a composite browser logger that forwards messages to multiple destinations.
 * Currently forwards to both Datadog and console logging.
 * 
 * @param currentEnvironment - The current environment (development, staging, production)
 * @returns Logger instance configured for the specified environment
 */
export const createTelemetryLogger = (currentEnvironment: Environment): Logger => {
  return {
    warn: (message, data) => {
      logToDatadog('warn', message, data);
      logToConsole('warn', message, data, { currentEnvironment });
    },
    error: (message, data) => {
      logToDatadog('error', message, data);
      logToConsole('error', message, data, { currentEnvironment });
    },
    debug: (message, data) => {
      logToConsole('debug', message, data, { currentEnvironment });
    },  
    info: (message, data) => {
      logToDatadog('info', message, data);
      logToConsole('info', message, data, { currentEnvironment });
    },
  };
};

/**
 * Creates a Logger instance that only logs to console.
 * Useful when you don't need external service logging.
 * 
 * @param currentEnvironment - The current environment (development, staging, production)
 * @param prefix - Optional custom prefix for log messages
 * @returns Logger instance that only uses console logging
 */
export const createConsoleLogger = (currentEnvironment: Environment, prefix?: string): Logger => {
  return {
    warn: (message, data) => {
      logToConsole('warn', message, data, { currentEnvironment, prefix });
    },
    error: (message, data) => {
      logToConsole('error', message, data, { currentEnvironment, prefix });
    },
    info: (message, data) => {
      logToConsole('info', message, data, { currentEnvironment, prefix });
    },
    debug: (message, data) => {
      logToConsole('debug', message, data, { currentEnvironment, prefix });
    },
  };
};
